'use client';

import { inter600 } from '@/config/fonts';
import InfoAlert from '@/shared/components/InfoAlert/InfoAlert';
import ManageFieldMapping from '@/containers/ContactLists/FieldMapping/ManageFieldMapping';
import SelectColumnForField from '@/containers/ContactLists/FieldMapping/SelectColumnForField';
import SelectFieldsToMap from '@/containers/ContactLists/FieldMapping/SelectFieldsToMap';
import {
  ColumnData,
  FieldData,
  FieldNameOptions,
  MappingData,
} from '@/containers/ContactLists/types';
import CloseButton from '@/shared/components/Buttons/CloseButton/CloseButton';
import { mappingFields } from '@/shared/utils/contact-list';
import classNames from 'classnames';
import { Dispatch, SetStateAction } from 'react';

interface P2PUploadStep4MappingProps {
  className?: string;
  mappingData: MappingData; // Object mapping field names to column numbers (as strings) or undefined if not mapped
  blurClassNames?: string; // Class names for applying blur effect (string)
  columnsData?: ColumnData[]; // Array of column data, or null if not available
  fieldsPendingMapping: FieldData[]; // Array of fields that still need to be mapped
  showMappingDialogForField?: FieldNameOptions; // Field name currently being mapped, or undefined if no dialog is shown
  columnsAvailableForMapping?: ColumnData[]; // Array of columns that can be selected for mapping
  setShowMappingDialogForField: (field: FieldNameOptions | undefined) => void; // Function to set the field for which the mapping dialog is shown
  setMappingData: Dispatch<SetStateAction<MappingData>>; // Function to update the mapping data
}

const P2PUploadStep4Mapping = ({
  className,
  mappingData,
  blurClassNames,
  columnsData,
  fieldsPendingMapping,
  showMappingDialogForField,
  columnsAvailableForMapping,
  setShowMappingDialogForField,
  setMappingData,
}: P2PUploadStep4MappingProps) => {
  return (
    <div className={classNames('', className)}>
      <div className="pb-4">
        <div className={classNames(inter600.className, 'text-2xl')}>
          Now, Map Other Columns
        </div>
        <div className={classNames('pt-2 text-sm text-gray-600 dark:text-gray-400')}>
          In this step you can map any other necessary columns in your contact list.
        </div>
      </div>

      <>
        <div className={classNames('flex flex-wrap gap-4', blurClassNames)}>
          {!columnsAvailableForMapping?.length && (
            <InfoAlert>
              There are no more columns available to map.
              <br />
              Please proceed to Submit your Contact list.
            </InfoAlert>
          )}
          {(Object.keys(mappingData) as FieldNameOptions[])
            .filter((md) => md !== 'lead_phone' && !!mappingData[md])
            .map((fieldName, idx) => (
              <ManageFieldMapping
                key={idx}
                fieldMappingData={{
                  ...(columnsData?.find(
                    (cd) =>
                      `${cd.columnNumber}` === (`${mappingData[fieldName]}` as string)
                  ) as ColumnData),
                  ...(mappingFields.find((mf) => mf.name === fieldName) as FieldData),
                }}
                onEdit={() => {
                  setShowMappingDialogForField(fieldName);
                }}
                onDelete={(d) => {
                  setMappingData((prev) => ({
                    ...prev,
                    [d]: undefined,
                  }));
                }}
              />
            ))}
          {!!fieldsPendingMapping.length && !!columnsAvailableForMapping?.length && (
            <SelectFieldsToMap
              fields={fieldsPendingMapping}
              onSelectField={setShowMappingDialogForField}
            />
          )}
        </div>
        {showMappingDialogForField && (
          <div className="bg-base-100 dark:bg-base-200 absolute top-6 right-6 bottom-6 left-6 z-50 flex flex-col gap-2 overflow-hidden rounded-2xl border border-gray-200 p-2 shadow-2xl dark:border-gray-700">
            <div className="flex h-16 min-h-16 items-center justify-between pr-4 pl-6">
              <span className={classNames('text-lg', inter600.className)}>
                Select a column for{' '}
                {mappingFields.find((f) => f.name === showMappingDialogForField)?.title}
              </span>
              <CloseButton onClose={() => setShowMappingDialogForField(undefined)} />
            </div>

            <div className="flex flex-wrap gap-2 overflow-auto px-6">
              {columnsAvailableForMapping?.map((c, idx) => (
                <SelectColumnForField
                  key={idx}
                  columnData={c}
                  onSelect={(columnData) => {
                    setMappingData((prev) => ({
                      ...prev,
                      [showMappingDialogForField]: `${columnData.columnNumber}`,
                    }));
                    setShowMappingDialogForField(undefined);
                  }}
                  selectionStyle="overlay"
                />
              ))}
            </div>
          </div>
        )}
      </>
    </div>
  );
};

export default P2PUploadStep4Mapping;
